import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
// evertel
import { Modal, ModalBody, Row, Col, InfoBox, Button, Icon, useUI, ModalHeader, Text } from '@evertel/web/ui'
import { copyToClipboard } from '@evertel/utils'
import { APIDataRoom } from '@evertel/types'
import { useService } from '@evertel/di'
import { SharedRoomController } from '@evertel/room'


interface ShareRoomModalProps {
    visible: boolean
    room: APIDataRoom
    onClose: () => void
    userIsGuest: boolean
}

const SharedRoomModal: React.FC<ShareRoomModalProps> = observer(({
    visible,
    room,
    onClose,
    userIsGuest
}) => {

    const sharedRoomController = useService(SharedRoomController, [room?.id])
    const { addToast, isConfirmed } = useUI()

    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(undefined)

    useEffect(() => {
        sharedRoomController.init(room?.id)

    }, [room?.id])

    useEffect(() => {
        if (visible) {
            //AnalyticsStore.logModalView('room-share')
        }
    }, [visible])

    const [announcement, setAnnouncement] = useState('')

    const createPersistentGuestInvite = async () => {
        setIsLoading(true)

        try {
            await sharedRoomController.createInvite()
            setAnnouncement('Room invite link has been created successfully')
        } catch (error) {
            addToast({
                color: 'danger',
                message: error.message
            })
            setAnnouncement('Failed to create room invite link')
        }

        setIsLoading(false)
    }

    const copy = (text) => {
        // copy text
        copyToClipboard(text)

        addToast({
            color: 'success',
            message: 'Copied to clipboard!'
        })
    }

    const deleteInvite = async (inviteId) => {
        const deleteIt = async () => {
            setIsLoading(true)

            try {
                await sharedRoomController.deleteInvite(inviteId)
                setAnnouncement('Room invite link has been revoked')
            } catch (error) {
                addToast({
                    color: 'danger',
                    message: error.message
                })
                setAnnouncement('Failed to revoke room invite link')
            }

            setIsLoading(false)
        }

        const confirmed = await isConfirmed({
            title: 'Delete Invite Link?',
            message: 'Are you sure you want to delete this shared invite?\n\nOnce deleted, anyone with the link or access code will not be able to join this room. Anyone already in this room will remain in this room.',
            acceptButton: {
                label: 'Delete',
                color: 'danger'
            }
        })
        if (confirmed) {
            deleteIt()
        }
    }

    if (!room) return null

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            size="lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-room-title"
            aria-describedby="share-room-description">
            <ModalHeader
                title={`Share "${room?.name}" with other agencies`}
                subtitle="In a Regional Collaboration room, people from other agencies can join a room and work together in a single space. Great for multi-agency events, cases, and crisis situations."
                id="share-room-title"
            />
            <ModalBody className="mb-3">
                <div
                    role="region"
                    aria-live="polite"
                    aria-atomic="true">
                    {announcement && (
                        <div className="visually-hidden" role="status">
                            {announcement}
                        </div>
                    )}
                    {(!sharedRoomController.latestActiveInvite) && (
                        <div id="share-room-description">
                            <Row className="my-4">
                                <Col>
                                    <InfoBox color="info">
                                        <Text bold>Things to keep in mind...</Text>
                                        <ul className="mt-2" role="list">
                                            <li className="mb-2">Any people outside your agency that join in a shared room will be guests and will only have access to this room</li>
                                            <li className="mb-2">When you share an existing room, the other agencies can view room members and room message history</li>
                                            <li className="mb-2">Members from both agencies can direct message each other</li>
                                            <li className="mb-2">When you broadcast the invite link or code, anyone that intercepts it can join the room. So, distribute with care</li>
                                            <li>Agencies you share the invite link with can distribute the link to the personnel they want to include on the room</li>
                                        </ul>
                                    </InfoBox>
                                </Col>
                            </Row>
                            <Row align="center">
                                <Button
                                    color="primary"
                                    size="lg"
                                    loading={isLoading}
                                    onClick={createPersistentGuestInvite}
                                    aria-label="Share room and create invite link">
                                    Share Room
                                </Button>
                            </Row>
                        </div>
                    )}
                    {(sharedRoomController.latestActiveInvite) && (
                        <InfoBox color="info">
                            <Text
                                tag="h4"
                                heavy
                                id="invite-status">
                                Anyone with this link can post and invite their coworkers
                            </Text>
                            <Text>
                                In a crisis situation, this Access Code could be broadcast over an encrypted radio channel for mass distribution
                                where emailing the link isn't feasible
                            </Text>
                            <div
                                className="mt-4 mb-2 code-block"
                                role="textbox"
                                aria-readonly="true"
                            >
                                <span className="visually-hidden">Room invite information:</span>
                                {sharedRoomController.latestActiveInvite.url}<br/><br/>
                                Access Code: {sharedRoomController.latestActiveInvite.token}
                            </div>
                            <div>
                                <Button
                                    color="info"
                                    className="me-4"
                                    onClick={() => copy(sharedRoomController.latestActiveInvite.url + '\n\nAccess Code: ' + sharedRoomController.latestActiveInvite.token)}
                                    aria-label="Copy invite link and access code to clipboard">
                                    <Icon
                                        name="copy"
                                        className="me-1"
                                        aria-hidden="true"
                                    />
                                    Copy Link
                                </Button>
                                {(!userIsGuest) && (
                                    <Button
                                        color="danger"
                                        outline
                                        onClick={() => deleteInvite(sharedRoomController.latestActiveInvite.id)}
                                    >
                                        <Icon
                                            name="minus-circle"
                                            className="me-1"
                                            aria-hidden="true"
                                        />
                                        Revoke Link
                                    </Button>
                                )}
                                <Text
                                    size="smaller"
                                    color="danger"
                                    className="float-right ms-4"
                                    role="status"
                                    aria-live="polite"
                                >
                                    Link expires on: {moment(sharedRoomController.latestActiveInvite.expiresDate).format('L HH:mm')}
                                </Text>
                            </div>
                            {(!userIsGuest) && (
                                <div className="mt-2">
                                    <Text size="smaller">
                                        When revoked, outside agencies will no longer be able to join this room but existing members will remain.
                                    </Text>
                                </div>
                            )}
                        </InfoBox>
                    )}
                </div>
            </ModalBody>
        </Modal>
    )
})

export { SharedRoomModal }
